import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { FormApi } from 'final-form';
import PopNotificationsContext from '../providers/PopNotifications/PopNotifications.context';
import set from 'lodash.set';
import get from 'lodash.get';
import StepsContext from '../providers/Steps/Steps.context';

type FormPatchingProps<PatchObj, Name extends string> = {
  readonly name: Name;
  patchCondition?: boolean;
  patchOnEdit?: boolean;
  initRequest?: () => Promise<
    AxiosResponse<{
      [key in Name]: PatchObj;
    }>
  >;
  patchRequest: (value: Partial<PatchObj>) => void;
  onSubmit?: (values: Record<string, any>) => void;
  onForbiddenRequest?: () => void;
  setEditedSteps?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  redirectOnPatch?: boolean;
};

export default function useFormPatching<
  PatchObj extends Record<string, any>,
  Name extends string,
>({
  name,
  patchCondition = true,
  onSubmit,
  initRequest,
  patchRequest,
  onForbiddenRequest,
  setEditedSteps,
  redirectOnPatch = true,
}: FormPatchingProps<PatchObj, Name>) {
  const { popServerError } = useContext(PopNotificationsContext);
  const initialized = useRef(false);
  const {
    currentStep,
    activeStep: activePatchStep,
    steps: { length: stepsNumber },
    nextStep,
  } = useContext(StepsContext);

  const [initialData, setInitialData] = useState<Partial<PatchObj>>({});
  const [loadingInitialData, setLoadingInitialData] = useState(false);
  const [loading, setLoading] = useState(false);

  const mNextStep = useCallback(
    (values: Partial<PatchObj>) => {
      setInitialData((old) => ({ ...old, ...values }));
      redirectOnPatch && nextStep();
    },
    [nextStep, redirectOnPatch],
  );

  useEffect(() => {
    if (!initRequest || initialized.current) return;

    (async () => {
      try {
        setLoadingInitialData(true);
        const { data } = await initRequest();
        setInitialData(data[name]);
      } catch (er) {
        popServerError(er);

        if ((er as AxiosError).response?.status === 403) {
          onForbiddenRequest?.();
        }
      } finally {
        setLoadingInitialData(false);
        initialized.current = true;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, popServerError, initRequest]);

  const patchData = useCallback(
    async (values: Partial<PatchObj>) => {
      const mappedValues: Partial<PatchObj> = activePatchStep?.patchMap
        ? (activePatchStep.patchMap(values) as Partial<PatchObj>)
        : values;

      try {
        setLoading(true);
        await patchRequest(mappedValues);
        setEditedSteps((old) => ({
          ...old,
          [activePatchStep.stepName]: false,
        }));
      } catch (er) {
        popServerError(er);
      } finally {
        setLoading(false);
      }
    },
    [activePatchStep, patchRequest, popServerError, setEditedSteps],
  );

  const onPatchStep = useCallback(
    async (values: PatchObj, form: FormApi<any>) => {
      const stepFieldKeys = Object.keys(form.getState().modified);

      const valuesFromThisStep = {};
      stepFieldKeys.forEach((key) =>
        set(valuesFromThisStep, key, get(values, key)),
      );

      if (activePatchStep?.skipPatch) {
        if (currentStep < stepsNumber - 1) {
          mNextStep(valuesFromThisStep);
        } else {
          await patchData(valuesFromThisStep);
        }
        return;
      }

      if (currentStep >= 0 && patchCondition) patchData(valuesFromThisStep);

      mNextStep(valuesFromThisStep);
    },
    [
      activePatchStep?.skipPatch,
      currentStep,
      stepsNumber,
      patchCondition,
      patchData,
      mNextStep,
    ],
  );

  const onFormSubmit = useCallback(
    async (values: PatchObj) => {
      onSubmit ? onSubmit(values) : patchData(values);
      setEditedSteps({});
    },
    [onSubmit, patchData, setEditedSteps],
  );

  return {
    initialData,
    loadingInitialData,
    onPatchStep,
    onFormSubmit,
    loading,
  } as const;
}
